import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Box,
  Rating,
  Button,
  Chip,
  Divider,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Snackbar
} from '@mui/material';
import {
  ShoppingCart,
  ArrowBack,
  Star,
  LocalShipping,
  AccessTime,
  CheckCircle,
  Add,
  Remove,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import MainService from '../services/MainService';
import config from '../services/config.json';

const ProductView = ({ addToCart }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // URL Parameters
  const searchParams = new URLSearchParams(location.search);
  const productNameFromUrl = searchParams.get('name');

  // States
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Fetch product details
  const fetchProduct = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await MainService.GetProductById(id);
      setProduct(response.data || response);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch similar products
  const fetchSimilarProducts = async () => {
    try {
      const response = await MainService.GetSimilarProductsById(id);
      setSimilarProducts(response.data || response || []);
    } catch (error) {
      console.error('Error fetching similar products:', error);
    }
  };

  // Call APIs on component mount
  useEffect(() => {
    fetchProduct();
    fetchSimilarProducts();
    // Reset quantity to 1 when product changes
    setQuantity(1);
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      addToCart({ ...product, quantity });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => 
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleViewProduct = (productId, productName) => {
    navigate(`/product/${productId}?name=${encodeURIComponent(productName)}`);
  };

  const handleAddToCartFromSimilar = (similarProduct) => {
    addToCart(similarProduct);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Get product images (assuming multiple images)
  const getProductImages = () => {
    if (!product) return [];
    
    const images = [];
    if (product.Image1) images.push(config.API_BASE_URL + product.Image1);
    if (product.Image2) images.push(config.API_BASE_URL + product.Image2);
    if (product.Image3) images.push(config.API_BASE_URL + product.Image3);
    if (product.Image4) images.push(config.API_BASE_URL + product.Image4);
    
    // If no images, use a placeholder
    if (images.length === 0) {
      images.push('https://via.placeholder.com/400x400?text=No+Image');
    }
    
    return images;
  };

  const productImages = getProductImages();

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h5" sx={{ textAlign: 'center', py: 8 }}>
          Product not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Products
      </Button>

      {/* Success Alert */}
      {showSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircle />}
        >
          Product added to cart successfully!
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Product Image */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            {/* Main Image with Navigation */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <CardMedia
                component="img"
                height="500"
                image={productImages[selectedImageIndex]}
                alt={product.Name || product.name || 'Product'}
                sx={{
                  objectFit: 'cover',
                  borderRadius: 2,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              />
              
              {/* Navigation Arrows */}
              {productImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: '#2E7D32',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: '#2E7D32',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Counter */}
            {productImages.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedImageIndex + 1} of {productImages.length}
                </Typography>
              </Box>
            )}

            {/* Thumbnail Images */}
            {productImages.length > 1 && (
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                justifyContent: 'center',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#2E7D32',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#1B5E20',
                },
              }}>
                                 {productImages.map((image, index) => (
                   <CardMedia
                     key={index}
                     component="img"
                     height="50"
                     width="200"
                     image={image}
                     alt={`Product ${index + 1}`}
                     sx={{
                       borderRadius: 1,
                       cursor: 'pointer',
                     }}
                     onClick={() => handleImageClick(index)}
                   />
                 ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Category Chip */}
            <Chip
              label={product.Category?.Name }
              color="primary"
              sx={{
                backgroundColor: '#2E7D32',
                color: 'white',
                mb: 2,
                alignSelf: 'flex-start'
              }}
            />

            {/* Product Name */}
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              {product.Name || product.name}
            </Typography>

            {/* Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating 
                value={product.Rating || product.rating || 4.5} 
                precision={0.1} 
                readOnly 
                sx={{ mr: 1 }}
              />
              <Typography variant="body1" sx={{ ml: 1 }}>
                {product.Rating || product.rating || 4.5} ({product.Reviews || product.reviews || 10} reviews)
              </Typography>
            </Box>

            {/* Price */}
            <Box sx={{ mb: 3 }}>
              {product.OfferPrice && product.Price && product.OfferPrice !== product.Price && (
                <Typography variant="h5" sx={{ textDecoration: 'line-through', color: 'text.secondary', mb: 1 }}>
                  ₹{product.Price || product.price || '0.00'}
                </Typography>
              )}
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                ₹{product.OfferPrice || product.offerPrice || product.Price || product.price || '0.00'}
              </Typography>
            </Box>

            {/* Description */}
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6 }}>
              {product.Description || product.description || 'No description available for this product.'}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Features */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Product Features
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle sx={{ color: '#2E7D32', mr: 1 }} />
                    <Typography variant="body2">Fresh from local farms</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle sx={{ color: '#2E7D32', mr: 1 }} />
                    <Typography variant="body2">Premium quality</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle sx={{ color: '#2E7D32', mr: 1 }} />
                    <Typography variant="body2">Organic certified</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle sx={{ color: '#2E7D32', mr: 1 }} />
                    <Typography variant="body2">Fast delivery</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />



                         {/* Add to Cart Section */}
             <Box sx={{ mt: 'auto' }}>
               <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                 Quantity
               </Typography>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <IconButton
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    type="number"
                    value={quantity}
                    onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 80 }}
                  />
                  <IconButton
                    onClick={() => handleQuantityChange(quantity + 1)}
                  >
                    <Add />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    Available in stock
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ShoppingCart />}
                    onClick={handleAddToCart}
                    sx={{
                      backgroundColor: '#2E7D32',
                      px: 3,
                      py: 1,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: '#1B5E20',
                      },
                    }}
                  >
                    Add to Cart - ₹{((product.OfferPrice || product.offerPrice || product.Price || product.price || 0) * quantity).toFixed(2)}
                  </Button>
                </Box>
             </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      
      {/* Similar Products Section */}
      {similarProducts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
            You might also like
          </Typography>
                     <Grid container spacing={3}>
             {similarProducts.map((similarProduct) => (
               <Grid item xs={12} sm={6} md={4} key={similarProduct.Id || similarProduct.id || Math.random()}>
              <Card
                sx={{
                  //height: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={config.API_BASE_URL + (similarProduct.Image1 || similarProduct.image1 || similarProduct.image || '')}
                  alt={similarProduct.Name || similarProduct.name || 'Product'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleViewProduct(similarProduct.Id, similarProduct.Name )}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {similarProduct.Name || similarProduct.name || 'Product Name'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {similarProduct.Size?.Name || 'Size'}  - {similarProduct.Category?.Name || similarProduct.category?.name || 'Category'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                      {similarProduct.OfferPrice && similarProduct.Price && similarProduct.OfferPrice !== similarProduct.Price && (
                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          ₹{similarProduct.Price || similarProduct.price || '0.00'}
                        </Typography>
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                        ₹{similarProduct.OfferPrice || similarProduct.offerPrice || similarProduct.Price || similarProduct.price || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default ProductView; 